import React from 'react';
import {
    isValidMeetingUrl,
    SUPPORTED_MEETING_PLATFORMS_LABEL,
} from 'utils/meetingDomains';
import styles from '../TargetGroupSettings.module.css';

interface MeetingUrlsEditorProps {
    urls: string[];
    onChange: (urls: string[]) => void;
}

/**
 * Editor for the list of `meeting_urls` shown in the Motiview tab.
 * Renders one input per URL with inline HTTPS-allow-list validation,
 * an "X" remove button per URL, and an "+ Add URL" button.
 *
 * Validation uses the shared `isValidMeetingUrl` so the rules stay
 * in sync with ScanSetupView/ForthStep.
 */
export const MeetingUrlsEditor = ({ urls, onChange }: MeetingUrlsEditorProps) => {
    const setUrlAt = (index: number, value: string) => {
        const updated = [...urls];
        updated[index] = value;
        onChange(updated);
    };

    const removeUrlAt = (index: number) => {
        onChange(urls.filter((_, i) => i !== index));
    };

    const addEmptyUrl = () => {
        onChange([...urls, '']);
    };

    const hasInvalidUrl = urls.some(
        (u) => u.trim() && !isValidMeetingUrl(u.trim()),
    );

    return (
        // textAlign:left forces left alignment regardless of the parent <td>'s
        // text-align (the table CSS gives a sole colspan=2 cell text-align:right
        // because both td:first-of-type and td:last-of-type apply, and the
        // latter wins on specificity tie).
        <div style={{ marginTop: 8, textAlign: 'left' }}>
            {/* Header row: title + description on the left, "+ Add URL" on the right */}
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 16,
                }}>
                <div style={{ flex: 1 }}>
                    <span
                        className={
                            styles.target_group_settings__pane__configuration__config_label
                        }>
                        Meeting URLs
                    </span>
                    <p
                        style={{
                            fontSize: 12,
                            color: '#666',
                            marginTop: 6,
                            marginBottom: 0,
                        }}>
                        {SUPPORTED_MEETING_PLATFORMS_LABEL}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={addEmptyUrl}
                    style={{
                        padding: '6px 16px',
                        borderRadius: 6,
                        border: '1px solid #ccc',
                        background: '#fff',
                        cursor: 'pointer',
                        fontSize: 14,
                        flexShrink: 0,
                    }}>
                    + Add URL
                </button>
            </div>

            {/* URL inputs render below the header when there are any */}
            {urls.length > 0 && (
                <div style={{ marginTop: 12 }}>
                    {urls.map((url, index) => {
                        const trimmed = url.trim();
                        const isInvalid =
                            trimmed.length > 0 && !isValidMeetingUrl(trimmed);
                        return (
                            <div
                                key={index}
                                style={{
                                    display: 'flex',
                                    gap: 8,
                                    marginBottom: 8,
                                    alignItems: 'center',
                                }}>
                                <input
                                    type="url"
                                    value={url}
                                    placeholder="https://calendly.com/..."
                                    onChange={(e) =>
                                        setUrlAt(index, e.target.value)
                                    }
                                    style={{
                                        flex: 1,
                                        padding: '8px 12px',
                                        borderRadius: 6,
                                        border: isInvalid
                                            ? '1px solid #f44336'
                                            : '1px solid #ccc',
                                        fontSize: 14,
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={() => removeUrlAt(index)}
                                    style={{
                                        padding: '6px 12px',
                                        borderRadius: 6,
                                        border: '1px solid #ccc',
                                        background: '#fff',
                                        cursor: 'pointer',
                                        fontSize: 16,
                                    }}>
                                    −
                                </button>
                            </div>
                        );
                    })}
                    {hasInvalidUrl && (
                        <p
                            style={{
                                color: '#f44336',
                                fontSize: 12,
                                marginTop: 4,
                                marginBottom: 0,
                            }}>
                            Invalid URL. Only HTTPS links from supported platforms
                            are allowed.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
};

export default MeetingUrlsEditor;
